import { useState, useEffect } from 'react';
import { Plus, Search, Mail, UserX, UserCheck } from 'lucide-react';
import { Button, Input, Card, Pagination, SortableHeader, AdvancedSearch, type SortDirection } from '../../components/ui';
import { API_BASE_URL, getAuthToken } from '../../lib/api';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  avatar?: string;
  lastLoginAt: string;
  createdAt: string;
}

const ROLE_OPTIONS = [
  { value: 'admin', label: '管理者' },
  { value: 'user', label: 'ユーザー' },
  { value: 'viewer', label: '閲覧者' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: '有効' },
  { value: 'pending', label: '保留' },
  { value: 'disabled', label: '無効' },
];

const SEARCH_FIELDS = [
  { key: 'name', label: '名前', type: 'text' as const, placeholder: '名前...' },
  { key: 'email', label: 'メールアドレス', type: 'text' as const, placeholder: 'email@example.com' },
  { key: 'role', label: 'ロール', type: 'select' as const, options: ROLE_OPTIONS },
  { key: 'status', label: 'ステータス', type: 'select' as const, options: STATUS_OPTIONS },
  { key: 'createdAt', label: '登録日', type: 'daterange' as const },
  { key: 'lastLoginAt', label: '最終ログイン', type: 'daterange' as const },
];

export function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // 検索条件
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  
  // ページネーション
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(30);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // ソート
  const [sortKey, setSortKey] = useState<string | null>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // モーダル
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [isInviting, setIsInviting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchUsers();
  }, [page, limit, sortKey, sortDirection]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(sortKey && sortDirection && { sortBy: sortKey, sortOrder: sortDirection }),
        ...Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== '')
        ),
      });
      
      const headers: Record<string, string> = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/users?${params}`, {
        credentials: 'include',
        headers,
      });
      const data = await response.json();
      
      setUsers(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error('Fetch users error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchUsers();
  };

  const handleReset = () => {
    setSearch('');
    setFilters({});
    setPage(1);
    fetchUsers();
  };

  const handleSort = (key: string, direction: SortDirection) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction);
    setPage(1);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsInviting(true);
    setMessage(null);

    try {
      const token = getAuthToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/users/invite`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '招待に失敗しました');
      }

      setMessage({ type: 'success', text: '招待メールを送信しました' });
      setInviteEmail('');
      setShowInviteModal(false);
      fetchUsers();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setIsInviting(false);
    }
  };

  const handleDisable = async (userId: string) => {
    if (!confirm('このユーザーを無効化しますか？')) return;

    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      await fetch(`${API_BASE_URL}/api/users/${userId}/disable`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });
      fetchUsers();
    } catch (err) {
      console.error('Disable user error:', err);
    }
  };

  const handleEnable = async (userId: string) => {
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      await fetch(`${API_BASE_URL}/api/users/${userId}/enable`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });
      fetchUsers();
    } catch (err) {
      console.error('Enable user error:', err);
    }
  };

  const getRoleBadge = (role: string) => {
    const badges: Record<string, string> = {
      admin: 'bg-purple-100 text-purple-700',
      user: 'bg-blue-100 text-blue-700',
      viewer: 'bg-gray-100 text-gray-700',
    };
    const labels: Record<string, string> = {
      admin: '管理者',
      user: 'ユーザー',
      viewer: '閲覧者',
    };
    return { className: badges[role] || badges.user, label: labels[role] || role };
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      active: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      disabled: 'bg-red-100 text-red-700',
    };
    const labels: Record<string, string> = {
      active: '有効',
      pending: '保留',
      disabled: '無効',
    };
    return { className: badges[status] || badges.pending, label: labels[status] || status };
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            ユーザー管理
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            {total.toLocaleString()} 名のユーザー
          </p>
        </div>
        <Button leftIcon={<Plus size={16} />} onClick={() => setShowInviteModal(true)}>
          ユーザーを招待
        </Button>
      </div>

      {/* メッセージ */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* 検索・フィルター */}
      <Card padding="sm">
        <AdvancedSearch
          fields={SEARCH_FIELDS}
          values={filters}
          onChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
          onSearch={handleSearch}
          onReset={handleReset}
        >
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="名前・メールアドレスで検索..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search size={18} />}
            />
          </div>
          <Button type="button" onClick={handleSearch}>
            検索
          </Button>
        </AdvancedSearch>
      </Card>

      {/* ユーザー一覧 */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <p style={{ color: 'var(--text-secondary)' }}>ユーザーがいません</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <SortableHeader
                    label="ユーザー"
                    sortKey="name"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="ロール"
                    sortKey="role"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="ステータス"
                    sortKey="status"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="最終ログイン"
                    sortKey="lastLoginAt"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <th className="text-right px-4 py-3 text-sm font-medium" style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--light-gray-primary)' }}>
                    アクション
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                {users.map((user) => {
                  const role = getRoleBadge(user.role);
                  const status = getStatusBadge(user.status);
                  
                  return (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {user.avatar ? (
                            <img src={user.avatar} alt="" className="w-10 h-10 rounded-full" />
                          ) : (
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                              style={{ backgroundColor: 'var(--accent-color)' }}
                            >
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                              {user.name}
                            </p>
                            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${role.className}`}>
                          {role.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${status.className}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {user.lastLoginAt 
                          ? new Date(user.lastLoginAt).toLocaleDateString('ja-JP')
                          : '未ログイン'
                        }
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          {user.status === 'active' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDisable(user.id)}
                              title="無効化"
                            >
                              <UserX size={16} />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEnable(user.id)}
                              title="有効化"
                            >
                              <UserCheck size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ページネーション */}
        {!isLoading && total > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
        )}
      </Card>

      {/* 招待モーダル */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              ユーザーを招待
            </h2>
            <form onSubmit={handleInvite} className="space-y-4">
              <Input
                label="メールアドレス"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="email@example.com"
                leftIcon={<Mail size={18} />}
                required
              />
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  ロール
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="user">ユーザー</option>
                  <option value="admin">管理者</option>
                  <option value="viewer">閲覧者</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowInviteModal(false)}
                >
                  キャンセル
                </Button>
                <Button type="submit" isLoading={isInviting}>
                  招待を送信
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
